// =====================================================
// USE PRICING LOOKUPS HOOK
// Degrees / districts / regions for pricing conditions (cached)
// =====================================================

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDegrees, fetchDistricts, fetchRegions } from '../utils/pricingRuleHelpers';
import type { ConditionLookups } from '@/models/pricingRule';

export function usePricingLookups() {
  const { data, isLoading } = useQuery({
    queryKey: ['pricingLookups'],
    queryFn: async () => {
      const [degrees, districts, regions] = await Promise.all([fetchDegrees(), fetchDistricts(), fetchRegions()]);
      return { degrees, districts, regions };
    },
    staleTime: 10 * 60 * 1000,
  });

  const degrees = useMemo(() => data?.degrees ?? [], [data]);
  const districts = useMemo(() => data?.districts ?? [], [data]);
  const regions = useMemo(() => data?.regions ?? [], [data]);

  // Undefined while loading or when a list failed to load, so rules aren't flagged by mistake
  const lookups = useMemo<ConditionLookups | undefined>(() => {
    if (!degrees.length || !districts.length || !regions.length) return undefined;
    return {
      degrees: new Map(degrees.map((d) => [d.id, d.name])),
      districts: new Map(districts.map((d) => [d.id, d.name ?? d.code ?? d.id])),
      regions: new Map(regions.map((r) => [r.id, r.name ?? r.code ?? r.id])),
    };
  }, [degrees, districts, regions]);

  return { degrees, districts, regions, lookups, isLoading };
}
